import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import CalculatorsPage from "@/pages/CalculatorsPage";
import ContactPage from "@/pages/ContactPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import BlogAdmin from "@/pages/BlogAdmin";
import BlogEditor from "@/pages/BlogEditor";
import ChangePassword from "@/pages/ChangePassword";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import CreateProposal from "@/pages/CreateProposal";
import ViewProposal from "@/pages/ViewProposal";
import UserManagement from "@/pages/UserManagement";
import ClearData from "@/pages/ClearData";
import RiskProfiling from "@/pages/RiskProfiling";
import CreateRiskProfile from "@/pages/CreateRiskProfile";
import ViewRiskProfile from "@/pages/ViewRiskProfile";
import InvestorOnboarding from "@/pages/InvestorOnboarding";
import CreateOnboarding from "@/pages/CreateOnboarding";
import NonIndividualOnboarding from "@/pages/NonIndividualOnboarding";
import ViewOnboarding from "@/pages/ViewOnboarding";
import PublicFormPage from "@/pages/PublicFormPage";
import PublicViewPage from "@/pages/PublicViewPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import DataRequest from "@/pages/DataRequest";
import AdminDataRequests from "@/pages/AdminDataRequests";
import CookieNotice from "@/components/CookieNotice";
import { RequireAuth } from "@/components/RequireAuth";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Layout wrapper for public pages
function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main className="pt-28">{children}</main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <CookieNotice />
        <Routes>
          {/* Public Routes with Header/Footer */}
          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
          <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
          <Route path="/calculators" element={<PublicLayout><CalculatorsPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
          <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
          <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
          <Route path="/data-request" element={<PublicLayout><DataRequest /></PublicLayout>} />

          {/* Public client self-fill links (no auth, no header/footer) */}
          <Route path="/fill/:kind/:token" element={<PublicFormPage />} />
          <Route path="/view/:kind/:token" element={<PublicViewPage />} />
          
          {/* Admin Routes (No Header/Footer) */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/proposals/create" element={<RequireAuth><CreateProposal /></RequireAuth>} />
          <Route path="/admin/proposals/:id" element={<RequireAuth><ViewProposal /></RequireAuth>} />
          <Route path="/admin/proposals/:id/edit" element={<RequireAuth><CreateProposal /></RequireAuth>} />
          <Route path="/admin/risk-profiling" element={<RequireAuth><RiskProfiling /></RequireAuth>} />
          <Route path="/admin/risk-profiling/create" element={<RequireAuth><CreateRiskProfile /></RequireAuth>} />
          <Route path="/admin/risk-profiling/view/:id" element={<RequireAuth><ViewRiskProfile /></RequireAuth>} />
          <Route path="/admin/onboarding" element={<RequireAuth><InvestorOnboarding /></RequireAuth>} />
          <Route path="/admin/onboarding/create" element={<RequireAuth><CreateOnboarding /></RequireAuth>} />
          <Route path="/admin/onboarding/create-entity" element={<RequireAuth><NonIndividualOnboarding /></RequireAuth>} />
          <Route path="/admin/onboarding/view/:id" element={<RequireAuth><ViewOnboarding /></RequireAuth>} />
          <Route path="/admin/users" element={<RequireAuth><UserManagement /></RequireAuth>} />
          <Route path="/admin/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
          <Route path="/admin/data-requests" element={<RequireAuth><AdminDataRequests /></RequireAuth>} />
          <Route path="/admin/clear-data" element={<RequireAuth><ClearData /></RequireAuth>} />
          <Route path="/admin/blog" element={<RequireAuth requireAdmin><BlogAdmin /></RequireAuth>} />
          <Route path="/admin/blog/create" element={<RequireAuth requireAdmin><BlogEditor /></RequireAuth>} />
          <Route path="/admin/blog/edit/:id" element={<RequireAuth requireAdmin><BlogEditor /></RequireAuth>} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
